import { all, take, select, takeEvery, call } from 'redux-saga/effects/';
import { get } from 'lodash';

import { getIsHotelOnly, searchTypeSelector } from 'src/store/view/selectors';
import { hitActualizePriceChange } from 'src/components/PriceChangePopup/analytics';
import { getIsFeatureHasAnOption } from 'src/utils/featureToggle';
import { getValueForExperimentDetectumVsRecommenderOtherPages } from 'src/utils/featureToggleSigma/analytics/analyticsDetectum';
import { dataLayerPush, dataLayerPushParams } from '../../../../helpers/gtm';

import { INIT_APP } from '../../../../store/view/constants';
import { LOG_IN } from '../../../../store/client/constants';
import {
  CLIENT_PATH,
  TOURISTS_PATH,
  PAY_PATH,
  SUCCESS_PATH,
} from '../constants';
import {
  ACTUALIZE_PACKAGE_FAIL,
  ACTUALIZE_PACKAGE_SUCCESS,
  START_ACTUALIZE_PACKAGE,
} from '../constants/actualization';
import {
  CREATE_ORDER_SUCCESS,
  SELECT_FLIGHT,
  CONFIRM_CLIENT,
  SET_FLIGHT_INFO,
  SUBMIT_TOURIST_SIMPLE_CHECKOUT,
} from '../constants/order';
import { GET_PACKAGES_FOR_ROOM_UPGRADE_SUCCESS } from '../constants/extra-rooms';
import { GO_TO_PATH, GO_TO_CLIENT, GO_TO_PAY } from '../constants/view';
import { TOGGLE_FLIGHT_CLASS } from '../../CheckoutRoutes/PackageCheckout/store/flights/constants';
import {
  SIMILAR_PACKAGES_START,
  SIMILAR_PACKAGES_FINISH,
  SIMILAR_PACKAGES_STATE,
  CREATE_PACKAGE,
  CREATE_PACKAGE_SUCCESS,
  SIMILAR_PACKAGES_POPUP_CLOSED,
  SIMILAR_PACKAGES_GO_TO_HOTEL,
  SIMILAR_PACKAGES_CLICK_ON_ALTERNATIVE,
  UPDATE_PACKAGE_ID,
} from '../constants/similar-packages';

import {
  getClientId,
  getCoefForRoomUpgrade,
  getIsLoggedIn,
  isPhoneConfirmedSelector,
} from '../../../../store/client/selectors';

import {
  getExtraRoomsPackageId,
  getExtraRoomsOriginalPackageId,
} from '../selectors/extra-rooms';

import {
  getPackageId,
  getSurcharge,
  makeGetKids,
  getAdultsCount,
  getTouristsCount,
  getStartDate,
  getOperatorName,
  getMealCode,
  getHotelId,
  getNetPrice,
  getRoomInfo,
  getMeal,
  getRegion,
  getNightsCount,
  // getIsManualPackage,
  getHotelLabels,
  getConfirmabilityIndex,
  getPackageObj,
  getOperatorId,
  getPriceRange,
} from '../selectors/package';

import {
  getHotelCountryIso2,
  getHotelStars,
  getHotelName,
  getHotelCountryName,
  getHotelPlace,
} from '../selectors/hotel';

import {
  origPackageMandatoryExtrasSelector,
  origPackageTOExtrasPriceSelector,
} from '../selectors/services';

import {
  cheapestFlightSelector,
  cheapestFlightOperatorIdSelector,
} from '../../CheckoutRoutes/PackageCheckout/store/flights/selectors';

import {
  getRemainingPaymentAmount,
  getIsRegularFlight,
  getSelectedFlightData,
  getOrderDiscountPrice,
  getOrderCoupon,
  getOrderBonusesGranted,
  getOrderId,
  getInsuranceType,
  getOrderFlightIsBusiness,
  getPriceOnPay,
  getPackageFullPrice,
} from '../selectors/order';

import { getPaymentMethod } from '../../../../store/payment/selectors';

import {
  getOriginDepartureFromFlight,
  getArrivalToDateFromFlight,
  flightsForAnalyticsSelector,
  filtersForAnalyticsSelector,
  flightsCountForAnalyticsSelector,
  shownOperatorsForAnalyticsSelector,
  origPacakgeMinFuelChargeSelector,
} from '../selectors/analytics';

import { getUpgradedFlight } from '../selectors/extras';

import { takeOnce } from '../../../../helpers/effects';
import {
  getSimilarPackagesState,
  getSimilarPackagesHasAdjustParams,
  getSimilarPackagesAdjustParams,
} from '../selectors/similar-packages';
import { formatServerDate } from '../../../../utils/dateUtils';
import type { goToPathAction } from '../actions/view';
import {
  altToursLabelIdsSelector,
  previousOperatorIdSelector,
  previousPacakgeIdSelector,
  selectedFlightWithAlternativesInfoSelector,
  toursAnalyticsSelector,
} from '../../CheckoutRoutes/PackageCheckout/store/actualize-alternatives';
import {
  flightsSetAction,
  fligthsFetchingDoneAction,
  origPackageFlightsFetchedAction,
} from '../../CheckoutRoutes/PackageCheckout/store/flights/slice';
import { frontErrorSelector } from '../selectors/actualization';
import { actualizePackageAction } from '../actions/actualization';
import { HIT_ACTUALIZE_PRICE_CHANGE } from '../constants/analytics';

const CATEGORY = {
  CHANGE_PARAMS: 'change_params',
  ROTTEN_PACKAGE: 'rotten_package',
  PACKAGE_PAGE: 'package_page',
  PAYMENT: 'PAYMENT',
};

function* setMarketingParams() {
  const hotelName = yield select(getHotelName);
  const countryRu = yield select(getHotelCountryName);
  const startDate = yield select(getStartDate);
  const regionNameRu = yield select(getHotelPlace);
  const pansionCode = yield select(getMealCode);
  const isRegularFlight = yield select(getIsRegularFlight);
  const selectedFlightData = yield select(getSelectedFlightData);
  const cheapestFlightData = yield select(cheapestFlightSelector);
  const paymentMethod = yield select(getPaymentMethod);
  const couponSum = yield select(getOrderDiscountPrice);
  const couponName = yield select(getOrderCoupon);
  const bonusesGranted = yield select(getOrderBonusesGranted);
  const orderId = yield select(getOrderId);
  const packageId = yield select(getPackageId);
  const fullAmount = yield select(getRemainingPaymentAmount);
  const netAmount = yield select(getNetPrice);
  const priceRange = yield select(getPriceRange);
  const paymentAmount = fullAmount || netAmount;

  // GTM Marketing params
  dataLayerPush({
    event: 'marketing_params',
    package_id: packageId,
    hotel_name_ru: hotelName,
    country_to_name_ru: countryRu,
    region_to_name_ru: regionNameRu,
    start_date: startDate,
    pansions_short_name: pansionCode,
    charter_flight: !isRegularFlight,
    flight_params: selectedFlightData || cheapestFlightData,
    order_id: orderId,
    payment_method: paymentMethod,
    order_sum: paymentAmount,
    coupon_name: couponName || null,
    coupon_sum: couponSum,
    bonuses_sum: bonusesGranted,
    price_range: priceRange,
  });

  if (orderId) {
    dataLayerPush({
      event: 'tour_payment',
    });
  }
}

function* setLoadedPage() {
  const surcharge = yield select(getSurcharge);
  const netPrice = yield select(getNetPrice);
  const priceFromHotelPage = surcharge + netPrice;

  dataLayerPushParams({ initial_price: priceFromHotelPage });
}

function* goToClient() {
  const isClient = yield select(getClientId);

  if (isClient) {
    dataLayerPush({ event: 'buyer_data' });
  }
}

function logIn() {
  dataLayerPush({ event: 'buyer_data' });
}

function* sendTouristsStep() {
  const searchType = yield select(searchTypeSelector);
  const isClientConfirmed = yield select(isPhoneConfirmedSelector);
  const isLoggedIn = yield select(getIsLoggedIn);

  if (isLoggedIn && isClientConfirmed)
    dataLayerPush({
      event: 'tourist_data',
      eventParams: {
        search_type: searchType,
      },
    });
}

function* changePage({ payload }: ReturnType<typeof goToPathAction>) {
  const isClient = yield select(getClientId);

  let pageEvent = '';

  switch (payload) {
    case CLIENT_PATH:
      return;
    case TOURISTS_PATH:
      yield setMarketingParams();
      if (!isClient) {
        pageEvent = 'auth_data';
      } else {
        return;
      }
      break;
    case PAY_PATH || SUBMIT_TOURIST_SIMPLE_CHECKOUT:
      yield setMarketingParams();
      pageEvent = 'payment_block';
      break;
    case SUCCESS_PATH:
      pageEvent = 'order_success';
      break;
    default:
      yield setMarketingParams();
      pageEvent = 'package';
      break;
  }

  dataLayerPush({
    event: pageEvent,
  });
}

function* selectFlight() {
  const selectedFlightInfo = yield select(
    selectedFlightWithAlternativesInfoSelector,
  );

  const package_id = yield select(getPackageId);
  const previous_package_id = yield select(previousPacakgeIdSelector);
  const packageObj = yield select(getPackageObj);
  const previousOperatorId = yield select(previousOperatorIdSelector);
  const abDetectum = yield call(
    getValueForExperimentDetectumVsRecommenderOtherPages,
  );

  dataLayerPush({
    event: 'start_booking',
    selectedFlight: selectedFlightInfo,
    packageId: String(package_id),
    previous_package_id: String(previous_package_id),
    packageObj,
    previous_operator_id: String(previousOperatorId),
    custom_params: {
      experiment_detectum_vs_recommender: abDetectum,
    },
  });
}

function* origPackageFlightsFetched() {
  const fullAmount = yield select(getRemainingPaymentAmount);
  const netAmount = yield select(getNetPrice);
  const paymentNetAmount = fullAmount || netAmount;

  const touristsCount = yield select(getTouristsCount);
  const kids = yield select(makeGetKids);

  const adultsCount = yield select(getAdultsCount);
  const startDate = yield select(getStartDate);
  const arrivalToDate = yield select(getArrivalToDateFromFlight);

  const operatorName = yield select(getOperatorName);
  const iso2Code = yield select(getHotelCountryIso2);

  const hotelId = yield select(getHotelId);
  const roomInfo = yield select(getRoomInfo);
  const originMeal = yield select(getMeal);
  const departureCity = yield select(getOriginDepartureFromFlight);
  const regionName = yield select(getRegion);
  const nightsCount = yield select(getNightsCount);
  const stars = yield select(getHotelStars);
  const labels = yield select(getHotelLabels);
  const confirmabilityIndex = yield select(getConfirmabilityIndex);
  const searchType = yield select(searchTypeSelector);

  const fullMandatoryExtrasPrice = yield select(
    origPackageTOExtrasPriceSelector,
  );
  const minFuelCharge = yield select(origPacakgeMinFuelChargeSelector);

  window.PACKAGE = {
    ...window.PACKAGE,
    surcharge: fullMandatoryExtrasPrice + minFuelCharge,
  };

  // package_params
  // flight_params приходят из GTM
  dataLayerPush({
    event: 'package_params',
    eventCategory: CATEGORY.PACKAGE_PAGE,
    eventAction: 'package_params',
    eventLabel: 'Package page initialized',
    eventParams: {
      event: 'package_params',
      hotel_id: hotelId,
      number_type: roomInfo.name_en,
      food_type: originMeal,
      operator_name: operatorName,
      page: window.location.href,
      departure_city: departureCity,
      country: iso2Code,
      region: regionName,
      flight_date: startDate,
      nights_count: nightsCount,
      arival_date: formatServerDate(arrivalToDate),
      adults_count: adultsCount,
      kids_count: kids && kids.length,
      kids_ages: kids,
      stars,
      price: paymentNetAmount,
      seen_price_per_night_person: Math.ceil(
        paymentNetAmount / touristsCount / nightsCount,
      ),
      labels: labels ? labels.map((label) => label.id).join(';') : null,
      confirmability_index: confirmabilityIndex,
      search_type: searchType,
    },
  });

  const mandatoryExtras = yield select(origPackageMandatoryExtrasSelector);
  const minimalFuelCharge = yield select(origPacakgeMinFuelChargeSelector);
  const fullPrice =
    mandatoryExtras.reduce(
      (extrasPrice, extra) => extrasPrice + extra.price,
      0,
    ) +
    minimalFuelCharge +
    netAmount;

  dataLayerPushParams({ new_price: fullAmount || fullPrice });

  yield setMarketingParams();
}

function* initApp() {
  const searchType = yield select(searchTypeSelector);
  window.PACKAGE = { ...window.PACKAGE, search_type: searchType };
  const abDetectum = yield call(
    getValueForExperimentDetectumVsRecommenderOtherPages,
  );
  dataLayerPush({
    event: 'ccx-pageview',
    custom_params: {
      experiment_detectum_vs_recommender: abDetectum,
    },
  });
}

function* setPaymentSuccess({ payload }) {
  window.ORDER = get(payload, 'data');
  const packageId = yield select(getPackageId);
  const paymentAmount = yield select(getRemainingPaymentAmount);
  const touristsCount = yield select(getTouristsCount);
  const nightsCount = yield select(getNightsCount);
  const roomInfo = yield select(getRoomInfo);
  const insuranceType = yield select(getInsuranceType);

  const orderId = payload.data.id;

  // Renaissance
  if (insuranceType === 'renins_cancellation') {
    dataLayerPush({ event: 'renaissance' });
  }

  const abDetectum = yield call(
    getValueForExperimentDetectumVsRecommenderOtherPages,
  );
  // GTM TRANSACTION
  dataLayerPush({
    event: 'transaction',
    transactionId: orderId,
    transactionAffiliation: 'level.travel',
    transactionTotal: paymentAmount,
    transactionTax: 0,
    transactionShipping: touristsCount,
    transactionProducts: [
      {
        sku: packageId,
        category: nightsCount,
        name: roomInfo.name_en,
        price: paymentAmount,
        quantity: 1,
      },
    ],
    custom_params: {
      experiment_detectum_vs_recommender: abDetectum,
    },
  });

  yield setMarketingParams();
}

function* packagesForRoomUpgradeLoaded() {
  const packages = yield select(getExtraRoomsPackageId);
  const original = yield select(getPackageId);
  const coef = yield select(getCoefForRoomUpgrade);

  if (!packages) {
    return;
  }

  dataLayerPush({
    event: 'upgrade_packages',
    upgrade_packages: {
      packages,
      original,
      coef,
    },
  });
}

// SIMILAR_PACKAGES
// https://leveltravel.atlassian.net/browse/LT-11120
function* similarPackagesStartSearch() {
  const hasAdjustParams = yield select(getSimilarPackagesHasAdjustParams);

  if (!hasAdjustParams) {
    return;
  }

  const startDate = yield select(getStartDate);
  const nightsCount = yield select(getNightsCount);
  const adultsCount = yield select(getAdultsCount);
  const kids = yield select(makeGetKids);
  const adjustParams = yield select(getSimilarPackagesAdjustParams);

  const packageParams = {
    currrent_params: {
      start_date: startDate,
      nights_count: nightsCount,
      adults_count: adultsCount,
      kids_ages: kids,
      kids_count: kids && kids.length,
    },
    new_params: {
      start_date: adjustParams.date,
      nights_count: adjustParams.nights,
      adults_count: adjustParams.adults,
      kids_ages: adjustParams.kidsAges,
      kids_count: adjustParams.kidsAges && adjustParams.kidsAges.length,
    },
  };

  dataLayerPush({
    event: 'find_packages',
    ...packageParams,
    eventCategory: CATEGORY.CHANGE_PARAMS,
  });
}

function* similarPackagesResult(action) {
  const { type } = action.payload;
  const state = yield select(getSimilarPackagesState);
  const hasAdjustParams = yield select(getSimilarPackagesHasAdjustParams);
  const isAlternativePackage = type === SIMILAR_PACKAGES_CLICK_ON_ALTERNATIVE;
  const eventCategory = hasAdjustParams
    ? CATEGORY.CHANGE_PARAMS
    : CATEGORY.ROTTEN_PACKAGE;
  let event;

  switch (state) {
    case SIMILAR_PACKAGES_STATE.NO_RESULTS:
      event = 'packages_not_found';
      break;
    case SIMILAR_PACKAGES_STATE.ALTERNATIVE:
      event = 'similar_packages_found';
      break;
    case SIMILAR_PACKAGES_STATE.EQUIVALENT:
      event = 'package_found';
      break;
    case SIMILAR_PACKAGES_STATE.REDIRECT:
      if (!isAlternativePackage) {
        return;
      }
      event = 'alternative_package';
      break;
    default:
      return;
  }

  dataLayerPush({ event, eventCategory });
}

function* similarPackagesCloseButton() {
  const state = yield select(getSimilarPackagesState);
  const hasAdjustParams = yield select(getSimilarPackagesHasAdjustParams);
  const eventCategory = hasAdjustParams
    ? CATEGORY.CHANGE_PARAMS
    : CATEGORY.ROTTEN_PACKAGE;

  if (state === SIMILAR_PACKAGES_STATE.ALTERNATIVE) {
    dataLayerPush({ event: 'close_similar_packages', eventCategory });
  }
}

function* similarPackagesHotelButton() {
  const hasAdjustParams = yield select(getSimilarPackagesHasAdjustParams);
  const eventCategory = hasAdjustParams
    ? CATEGORY.CHANGE_PARAMS
    : CATEGORY.ROTTEN_PACKAGE;

  dataLayerPush({ event: 'hotel_packages', eventCategory });
}

// После создания пакета ждём завершения актуализации,
// при успешной актуализации отправить данные о новом туре
function* similarPackagesActualization() {
  yield take(origPackageFlightsFetchedAction.type);
  yield origPackageFlightsFetched();
}

// Пользователь увидел блок "Улучшить до бизнесс-класса"
function* showUpgradeFlightToBusiness() {
  yield take(GO_TO_PAY);

  const hasUpgradedFlight = yield select(getUpgradedFlight);

  if (hasUpgradedFlight) {
    dataLayerPush({ event: 'upgrade_business' });
  }
}

// Выбран рейс в блоке "Улучшить до бизнесс-класса"
function* upgradeFlightToBusiness() {
  yield take(SET_FLIGHT_INFO);
  const isBusiness = yield select(getOrderFlightIsBusiness);

  const event = isBusiness
    ? 'upgrade_to_business_class'
    : 'return_economy_class';
  dataLayerPush({ event });
}

// Блок "Улучшить выбранный номер"
function* upgradeToExtraRoom() {
  const originalPackageId = yield select(getExtraRoomsOriginalPackageId);
  const updatedPackageId = yield select(getPackageId);

  const hasSelectedNewPackage = originalPackageId !== updatedPackageId;
  const event = hasSelectedNewPackage ? 'room_upgrade' : 'room_return';
  dataLayerPush({ event });
}

// Полеты и их фильтры
function* applyFlightsFilters() {
  const filters = yield select(filtersForAnalyticsSelector);
  const flights = yield select(flightsForAnalyticsSelector);

  window.PACKAGE.filters = filters;
  window.FLIGHTS = {
    flight_options: JSON.stringify(flights).replace(/;|,/g, '|'),
  };

  dataLayerPush({ event: 'ccx-event' });
}

function* handleSendActualizationStatus({ type }) {
  const countryName = yield select(getHotelCountryName);
  const packageId = yield select(getPackageId);
  const operatorName = yield select(getOperatorName);
  const error = yield select(frontErrorSelector) || 'unknown_error';

  let eventName;

  switch (type) {
    case actualizePackageAction.type:
      eventName = 'actualize_start';
      break;
    case ACTUALIZE_PACKAGE_SUCCESS:
      eventName = 'actualize_success';
      break;
    case ACTUALIZE_PACKAGE_FAIL:
      eventName = 'actualize_failed';
      break;
    default:
  }

  dataLayerPush({
    event: eventName,
    countryName,
    packageId: String(packageId),
    operatorName,
    ...(eventName === 'actualize_failed' ? { error } : {}),
  });
}

/**
 * Отправка инфы про актуализацию соседних рейсов
 */
function* handleFlightsFetchingDone() {
  const isHotelOnly = yield select(getIsHotelOnly);
  const actualizeAltDisabled = getIsFeatureHasAnOption(
    'actualize_alternatives',
    'off',
  );

  if (isHotelOnly || actualizeAltDisabled) {
    return;
  }

  const altToursAnalyticInfo = yield select(toursAnalyticsSelector);
  const shownOperators = yield select(shownOperatorsForAnalyticsSelector);
  const cheapestFlightOperatorId = yield select(
    cheapestFlightOperatorIdSelector,
  );

  const flightsStats = yield select(flightsCountForAnalyticsSelector);

  const operatorId = yield select(getOperatorId);

  const altToursLabelIds = yield select(altToursLabelIdsSelector);

  const customParams = {
    ...altToursAnalyticInfo,
    shown_operators: shownOperators,
    original_operator: operatorId,
    cheapest_operator: cheapestFlightOperatorId,
    flights_by_operators_keys: flightsStats.flightsByOperators.keys,
    flights_by_operators_values: flightsStats.flightsByOperators.values,
    original_package_flights_count: flightsStats.originalPackageFlightsCount,
    alt_packages_flights_count: flightsStats.altPackagesFlightsCount,
    labels: altToursLabelIds,
  };

  dataLayerPush({
    event: 'ccx-actualize_alternatives_finish',
    eventData: {
      package_params: window.PACKAGE,
      custom_params: customParams,
    },
  });
}

function* handleHitActualizePriceChange() {
  const oldPrice = yield select(getPriceOnPay);
  const newPrice = yield select(getPackageFullPrice);
  const packageId = yield select(getPackageId);
  const operatorId = yield select(getOperatorId);
  const operatorName = yield select(getOperatorName);
  const countryIso2 = yield select(getHotelCountryIso2);

  hitActualizePriceChange({
    packageId,
    operator: {
      id: operatorId,
      name: operatorName,
    },
    oldPrice,
    newPrice,
    hotelCountryIso2: countryIso2,
  });
}

export default function* analyticsCheckoutWatcher() {
  yield all([
    takeOnce(INIT_APP, initApp),
    takeEvery(GO_TO_PATH, changePage),
    takeEvery(GO_TO_CLIENT, goToClient),
    takeEvery(SELECT_FLIGHT, selectFlight),
    takeEvery([GO_TO_CLIENT, CONFIRM_CLIENT], sendTouristsStep),
    takeEvery(LOG_IN, logIn),
    takeOnce(START_ACTUALIZE_PACKAGE, setLoadedPage),
    takeEvery(
      [
        actualizePackageAction.type,
        ACTUALIZE_PACKAGE_SUCCESS,
        ACTUALIZE_PACKAGE_FAIL,
      ],
      handleSendActualizationStatus,
    ),
    takeOnce(origPackageFlightsFetchedAction.type, origPackageFlightsFetched),
    takeOnce(CREATE_ORDER_SUCCESS, setPaymentSuccess),
    // SIMILAR EVENTS
    takeEvery(SIMILAR_PACKAGES_START, similarPackagesStartSearch),
    takeEvery([SIMILAR_PACKAGES_FINISH, CREATE_PACKAGE], similarPackagesResult),
    takeEvery(SIMILAR_PACKAGES_POPUP_CLOSED, similarPackagesCloseButton),
    takeEvery(SIMILAR_PACKAGES_GO_TO_HOTEL, similarPackagesHotelButton),
    takeEvery(CREATE_PACKAGE_SUCCESS, similarPackagesActualization),
    // UPGRADE TO BUSINESS
    takeEvery([INIT_APP, CREATE_PACKAGE_SUCCESS], showUpgradeFlightToBusiness),
    takeEvery(TOGGLE_FLIGHT_CLASS, upgradeFlightToBusiness),
    // UPGRADE TO EXTRA ROOM
    takeEvery(
      GET_PACKAGES_FOR_ROOM_UPGRADE_SUCCESS,
      packagesForRoomUpgradeLoaded,
    ),
    takeEvery(UPDATE_PACKAGE_ID, upgradeToExtraRoom),
    // FLIGHT FILTERS
    takeEvery(flightsSetAction.type, applyFlightsFilters),
    // ACTUALIZE ALTERNATIVES
    takeOnce(fligthsFetchingDoneAction.type, handleFlightsFetchingDone),
    takeEvery(HIT_ACTUALIZE_PRICE_CHANGE, handleHitActualizePriceChange),
  ]);
}
